/* eslint-disable @typescript-eslint/no-unused-vars */

import {
  axios,
  AxiosResponsePromise,
  AxiosRequestExtraConfig,
  AxiosRequestConfig,
} from '@seiue/axios'
import {
  createConfigBuilder,
  useQueryApi,
  useMutationApi,
  UseQueryApiReturns,
} from '../utils'
import { MutationOptions, QueryOptions, Expand } from '../types'
import { UrlHash } from './url-hash'
import { UrlHashReq } from './url-hash-req'
import { SystemLog } from './system-log'
import { SystemReflection } from './system-reflection'
import { SystemReflectionReq } from './system-reflection-req'
import { SystemReflectionStatusEnum } from './system-reflection-status-enum'
import { School } from './school'
import { SchoolReq } from './school-req'
import { UpdatePassReq } from './update-pass-req'
import { User } from './user'
import { LoadUrlHashQuery } from './load-url-hash-query'
import { QueryZeusLogsQuery } from './query-zeus-logs-query'
import { QuerySystemReflectionsQuery } from './query-system-reflections-query'
import { LoadSystemReflectionQuery } from './load-system-reflection-query'
import { QuerySchoolsQuery } from './query-schools-query'
import { LoadZeusSchoolQuery } from './load-zeus-school-query'

const buildConfig = createConfigBuilder('growp')

export const zeusApi = {
  loadUrlHashConfig(
    loadUrlHashQuery?: LoadUrlHashQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'get',
      '/urlhash/urlhashs',
      {},
      loadUrlHashQuery
    )
  },

  /**
   * @summary 查询 学校地址加密记录
   * @param [loadUrlHashQuery]
   * @param [loadUrlHashQuery.hash]
   * @param [loadUrlHashQuery.activityId]
   * @param [options]
   * @return AxiosResponsePromise<UrlHash | null>
   */
  loadUrlHash(
    loadUrlHashQuery?: LoadUrlHashQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<UrlHash | null> {
    return axios(zeusApi.loadUrlHashConfig(loadUrlHashQuery, options)).then(
      ({ data: _data, ...rest }) => {
        const data = _data ? new UrlHash(_data) : _data
        return { data, ...rest }
      }
    ) as any
  },

  useLoadUrlHash(
    query?: LoadUrlHashQuery,
    queryOptions?: QueryOptions
  ): UseQueryApiReturns<UrlHash | null> {
    return useQueryApi<UrlHash | null>(
      zeusApi.loadUrlHash,
      'Zeus.loadUrlHash',
      queryOptions,
      query
    )
  },

  createUrlHashConfig(
    urlHashReq: UrlHashReq,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'post',
      '/urlhash/urlhashs',
      {},
      null,
      urlHashReq,
      {
        format: 'json',
        isArray: false,
        defaults: {},
      }
    )
  },

  /**
   * @summary 新增 学校地址加密记录
   * @param urlHashReq
   * @param [options]
   * @return AxiosResponsePromise<UrlHash>
   */
  createUrlHash(
    urlHashReq: UrlHashReq,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<UrlHash> {
    return axios(zeusApi.createUrlHashConfig(urlHashReq, options)).then(
      ({ data: _data, ...rest }) => {
        const data = _data ? new UrlHash(_data) : _data
        return { data, ...rest }
      }
    ) as any
  },

  useCreateUrlHash(mutationOptions?: MutationOptions) {
    return useMutationApi(
      (payload: UrlHashReq) => zeusApi.createUrlHash(payload),
      mutationOptions
    )
  },

  updateUrlHashConfig(
    id: number,
    urlHashReq: UrlHashReq,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'put',
      '/urlhash/urlhashs/{id}',
      {
        id,
      },
      null,
      urlHashReq,
      {
        format: 'json',
        isArray: false,
        defaults: {},
      }
    )
  },

  /**
   * @summary 新增 学校地址加密记录
   * @param id
   * @param urlHashReq
   * @param [options]
   * @return AxiosResponsePromise<UrlHash>
   */
  updateUrlHash(
    id: number,
    urlHashReq: UrlHashReq,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<UrlHash> {
    return axios(zeusApi.updateUrlHashConfig(id, urlHashReq, options)).then(
      ({ data: _data, ...rest }) => {
        const data = _data ? new UrlHash(_data) : _data
        return { data, ...rest }
      }
    ) as any
  },

  useUpdateUrlHash(mutationOptions?: MutationOptions) {
    return useMutationApi(
      ({ id, payload }: { id: number; payload: UrlHashReq }) =>
        zeusApi.updateUrlHash(id, payload),
      mutationOptions
    )
  },

  queryZeusLogsConfig(
    queryZeusLogsQuery?: QueryZeusLogsQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(options, 'get', '/zeus/logs', {}, queryZeusLogsQuery)
  },

  /**
   * @summary 大后台-日志查询
   * @param [queryZeusLogsQuery]
   * @param [queryZeusLogsQuery.eventAction]
   * @param [queryZeusLogsQuery.paginated] 是否分页，默认 1
   * @param [queryZeusLogsQuery.page] 显示的数据页，默认 1
   * @param [queryZeusLogsQuery.perPage] 每页显示数量，默认 20
   * @param [queryZeusLogsQuery.idIn]
   * @param [queryZeusLogsQuery.operatorLike] 操作人
   * @param [queryZeusLogsQuery.operatorUsinLike] 操作学工号
   * @param [queryZeusLogsQuery.operatorAccountLike] 操作账号
   * @param [queryZeusLogsQuery.operatedAtEgt] 开始时间
   * @param [queryZeusLogsQuery.operatedAtElt] 结束时间
   * @param [queryZeusLogsQuery.summaryLike] 操作内容
   * @param [queryZeusLogsQuery.labelsDotBizId] 业务 ID
   * @param [queryZeusLogsQuery.ipLike] IP 模糊查询
   * @param [options]
   * @return AxiosResponsePromise<SystemLog[]>
   */
  queryZeusLogs(
    queryZeusLogsQuery?: QueryZeusLogsQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<SystemLog[]> {
    return axios(zeusApi.queryZeusLogsConfig(queryZeusLogsQuery, options)).then(
      ({ data: _data, ...rest }) => {
        const data = _data ? _data.map((d: any) => new SystemLog(d)) : _data
        return { data, ...rest }
      }
    ) as any
  },

  useQueryZeusLogs(
    query?: QueryZeusLogsQuery,
    queryOptions?: QueryOptions
  ): UseQueryApiReturns<SystemLog[]> {
    return useQueryApi<SystemLog[]>(
      zeusApi.queryZeusLogs,
      'Zeus.queryZeusLogs',
      queryOptions,
      query
    )
  },

  querySystemReflectionsConfig(
    querySystemReflectionsQuery?: QuerySystemReflectionsQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'get',
      '/zeus/reflections',
      {},
      querySystemReflectionsQuery
    )
  },

  /**
   * @summary 大后台-用户查询
   * @param [querySystemReflectionsQuery]
   * @param [querySystemReflectionsQuery.status] 状态查询
   * @param [querySystemReflectionsQuery.expand]
   * @param [querySystemReflectionsQuery.paginated] 是否分页，默认 1
   * @param [querySystemReflectionsQuery.page] 显示的数据页，默认 1
   * @param [querySystemReflectionsQuery.perPage] 每页显示数量，默认 20
   * @param [querySystemReflectionsQuery.sort]
   * @param [querySystemReflectionsQuery.nameIn]
   * @param [querySystemReflectionsQuery.nameLike] 名字模糊查询
   * @param [querySystemReflectionsQuery.accountIn] 多个账户查询
   * @param [querySystemReflectionsQuery.accountLike] 账户模糊查询
   * @param [querySystemReflectionsQuery.phone]
   * @param [querySystemReflectionsQuery.phoneLike] 手机号模糊查询
   * @param [querySystemReflectionsQuery.phoneIn]
   * @param [querySystemReflectionsQuery.usinIn] 学工号查询
   * @param [querySystemReflectionsQuery.usinLike] 学工号模糊查询
   * @param [querySystemReflectionsQuery.createdAtEgt] 创建时间大于
   * @param [querySystemReflectionsQuery.createdAtElt] 创建时间小于
   * @param [querySystemReflectionsQuery.statusIn]
   * @param [querySystemReflectionsQuery.roleIn]
   * @param [querySystemReflectionsQuery.idIn]
   * @param [options]
   * @return AxiosResponsePromise<SystemReflection[]>
   */
  querySystemReflections<TQuery extends QuerySystemReflectionsQuery>(
    querySystemReflectionsQuery?: TQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<Expand<SystemReflection, TQuery['expand']>[]> {
    return axios(
      zeusApi.querySystemReflectionsConfig(querySystemReflectionsQuery, options)
    ).then(({ data: _data, ...rest }) => {
      const data = _data
        ? _data.map((d: any) => new SystemReflection(d))
        : _data
      return { data, ...rest }
    }) as any
  },

  useQuerySystemReflections<TQuery extends QuerySystemReflectionsQuery>(
    query?: TQuery,
    queryOptions?: QueryOptions
  ): UseQueryApiReturns<Expand<SystemReflection, TQuery['expand']>[]> {
    return useQueryApi<Expand<SystemReflection, TQuery['expand']>[]>(
      zeusApi.querySystemReflections,
      'Zeus.querySystemReflections',
      queryOptions,
      query
    )
  },

  createSystemReflectionConfig(
    systemReflectionReq: SystemReflectionReq,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'post',
      '/zeus/reflections',
      {},
      null,
      systemReflectionReq,
      {
        format: 'json',
        isArray: false,
        defaults: {
          name: (v: any) => v || '',
          account: (v: any) => v || '',
          phone: (v: any) => v || '',
          password: (v: any) => v || '',
          status: (v: any) => v || null,
          type: (v: any) => v || null,
          email: (v: any) => v || '',
          avatar: (v: any) => v || '',
          remark: (v: any) => v || '',
          managedSchoolIds: (v: any) => (v === 0 ? v : v || null),
        },
      }
    )
  },

  /**
   * @summary 大后台-用户添加（目前仅超管可以添加管理员）
   * @param systemReflectionReq
   * @param [options]
   * @return AxiosResponsePromise<SystemReflection>
   */
  createSystemReflection(
    systemReflectionReq: SystemReflectionReq,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<SystemReflection> {
    return axios(
      zeusApi.createSystemReflectionConfig(systemReflectionReq, options)
    ).then(({ data: _data, ...rest }) => {
      const data = _data ? new SystemReflection(_data) : _data
      return { data, ...rest }
    }) as any
  },

  useCreateSystemReflection(mutationOptions?: MutationOptions) {
    return useMutationApi(
      (payload: SystemReflectionReq) => zeusApi.createSystemReflection(payload),
      mutationOptions
    )
  },

  loadSystemReflectionConfig(
    id: number,
    loadSystemReflectionQuery?: LoadSystemReflectionQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'get',
      '/zeus/reflections/{id}',
      {
        id,
      },
      loadSystemReflectionQuery
    )
  },

  /**
   * @summary 大后台-单用户查询
   * @param id
   * @param [loadSystemReflectionQuery]
   * @param [loadSystemReflectionQuery.expand]
   * @param [options]
   * @return AxiosResponsePromise<SystemReflection>
   */
  loadSystemReflection<TQuery extends LoadSystemReflectionQuery>(
    id: number,
    loadSystemReflectionQuery?: TQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<Expand<SystemReflection, TQuery['expand']>> {
    return axios(
      zeusApi.loadSystemReflectionConfig(id, loadSystemReflectionQuery, options)
    ).then(({ data: _data, ...rest }) => {
      const data = _data ? new SystemReflection(_data) : _data
      return { data, ...rest }
    }) as any
  },

  useLoadSystemReflection<TQuery extends LoadSystemReflectionQuery>(
    {
      id,
      query,
    }: {
      id: number
      query?: TQuery
    },
    queryOptions?: QueryOptions
  ): UseQueryApiReturns<Expand<SystemReflection, TQuery['expand']>> {
    return useQueryApi<Expand<SystemReflection, TQuery['expand']>>(
      zeusApi.loadSystemReflection,
      'Zeus.loadSystemReflection',
      queryOptions,
      id,
      query
    )
  },

  updateSystemReflectionConfig(
    id: number,
    systemReflectionReq: SystemReflectionReq,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'put',
      '/zeus/reflections/{id}',
      {
        id,
      },
      null,
      systemReflectionReq,
      {
        format: 'json',
        isArray: false,
        defaults: {
          name: (v: any) => v || '',
          account: (v: any) => v || '',
          phone: (v: any) => v || '',
          password: (v: any) => v || '',
          status: (v: any) => v || null,
          type: (v: any) => v || null,
          email: (v: any) => v || '',
          avatar: (v: any) => v || '',
          remark: (v: any) => v || '',
          managedSchoolIds: (v: any) => (v === 0 ? v : v || null),
        },
      }
    )
  },

  /**
   * @summary 大后台-用户修改（目前仅超管可以修改管理员）
   * @param id
   * @param systemReflectionReq
   * @param [options]
   * @return AxiosResponsePromise<SystemReflection>
   */
  updateSystemReflection(
    id: number,
    systemReflectionReq: SystemReflectionReq,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<SystemReflection> {
    return axios(
      zeusApi.updateSystemReflectionConfig(id, systemReflectionReq, options)
    ).then(({ data: _data, ...rest }) => {
      const data = _data ? new SystemReflection(_data) : _data
      return { data, ...rest }
    }) as any
  },

  useUpdateSystemReflection(mutationOptions?: MutationOptions) {
    return useMutationApi(
      ({ id, payload }: { id: number; payload: SystemReflectionReq }) =>
        zeusApi.updateSystemReflection(id, payload),
      mutationOptions
    )
  },

  deleteSystemReflectionConfig(
    id: number,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(options, 'delete', '/zeus/reflections/{id}', {
      id,
    })
  },

  /**
   * @summary 大后台-用户删除（目前仅超管可以删除管理员）
   * @param id
   * @param [options]
   * @return AxiosResponsePromise<null>
   */
  deleteSystemReflection(
    id: number,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<null> {
    return axios(zeusApi.deleteSystemReflectionConfig(id, options)) as any
  },

  useDeleteSystemReflection(mutationOptions?: MutationOptions) {
    return useMutationApi(
      (id: number) => zeusApi.deleteSystemReflection(id),
      mutationOptions
    )
  },

  updateSystemReflectionStatusConfig(
    status: SystemReflectionStatusEnum,
    id: number,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'put',
      '/zeus/reflections/{id}/status/{status}',
      {
        status,
        id,
      }
    )
  },

  /**
   * @summary 大后台-用户状态修改（目前仅超管可以修改管理员）
   * @param status
   * @param id
   * @param [options]
   * @return AxiosResponsePromise<boolean>
   */
  updateSystemReflectionStatus(
    status: SystemReflectionStatusEnum,
    id: number,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<boolean> {
    return axios(
      zeusApi.updateSystemReflectionStatusConfig(status, id, options)
    ) as any
  },

  useUpdateSystemReflectionStatus(mutationOptions?: MutationOptions) {
    return useMutationApi(
      ({ status, id }: { status: SystemReflectionStatusEnum; id: number }) =>
        zeusApi.updateSystemReflectionStatus(status, id),
      mutationOptions
    )
  },

  querySchoolsConfig(
    querySchoolsQuery?: QuerySchoolsQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(options, 'get', '/zeus/schools', {}, querySchoolsQuery)
  },

  /**
   * @summary 大后台-学校查询
   * @param [querySchoolsQuery]
   * @param [querySchoolsQuery.paginated] 是否分页，默认 1
   * @param [querySchoolsQuery.page] 显示的数据页，默认 1
   * @param [querySchoolsQuery.perPage] 每页显示数量，默认 20
   * @param [querySchoolsQuery.sort]
   * @param [querySchoolsQuery.nameIn]
   * @param [querySchoolsQuery.nameLike] 名字模糊查询
   * @param [querySchoolsQuery.idIn]
   * @param [querySchoolsQuery.identityLike] 标识符模糊查询
   * @param [querySchoolsQuery.urlLike] 域名模糊查询
   * @param [querySchoolsQuery.systemVerIn] 版本查询
   * @param [querySchoolsQuery.statusIn] 状态查询
   * @param [querySchoolsQuery.expand]
   * @param [options]
   * @return AxiosResponsePromise<School[]>
   */
  querySchools<TQuery extends QuerySchoolsQuery>(
    querySchoolsQuery?: TQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<Expand<School, TQuery['expand']>[]> {
    return axios(zeusApi.querySchoolsConfig(querySchoolsQuery, options)).then(
      ({ data: _data, ...rest }) => {
        const data = _data ? _data.map((d: any) => new School(d)) : _data
        return { data, ...rest }
      }
    ) as any
  },

  useQuerySchools<TQuery extends QuerySchoolsQuery>(
    query?: TQuery,
    queryOptions?: QueryOptions
  ): UseQueryApiReturns<Expand<School, TQuery['expand']>[]> {
    return useQueryApi<Expand<School, TQuery['expand']>[]>(
      zeusApi.querySchools,
      'Zeus.querySchools',
      queryOptions,
      query
    )
  },

  createSchoolConfig(
    schoolReq: SchoolReq,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(options, 'post', '/zeus/schools', {}, null, schoolReq, {
      format: 'json',
      isArray: false,
      defaults: {
        name: (v: any) => v || '',
        identity: (v: any) => v || '',
        url: (v: any) => v || '',
        systemVer: (v: any) => v || null,
        status: (v: any) => v || null,
        hidden: (v: any) => v || false,
        expiredAt: (v: any) => v || '',
        description: (v: any) => v || '',
        logo: (v: any) => v || '',
        bgImage: (v: any) => v || '',
        themeColor: (v: any) => v || '',
        openMsgCheck: (v: any) => v || false,
        manager: (v: any) => v || null,
      },
    })
  },

  /**
   * @summary 大后台-学校单个创建(目前仅超管可创建)
   * @param schoolReq
   * @param [options]
   * @return AxiosResponsePromise<School>
   */
  createSchool(
    schoolReq: SchoolReq,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<School> {
    return axios(zeusApi.createSchoolConfig(schoolReq, options)).then(
      ({ data: _data, ...rest }) => {
        const data = _data ? new School(_data) : _data
        return { data, ...rest }
      }
    ) as any
  },

  useCreateSchool(mutationOptions?: MutationOptions) {
    return useMutationApi(
      (payload: SchoolReq) => zeusApi.createSchool(payload),
      mutationOptions
    )
  },

  deleteSchoolConfig(
    id: number,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(options, 'delete', '/zeus/schools/{id}', {
      id,
    })
  },

  /**
   * @summary 大后台-学校单个删除(目前仅超管可删除)
   * @param id
   * @param [options]
   * @return AxiosResponsePromise<null>
   */
  deleteSchool(
    id: number,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<null> {
    return axios(zeusApi.deleteSchoolConfig(id, options)) as any
  },

  useDeleteSchool(mutationOptions?: MutationOptions) {
    return useMutationApi(
      (id: number) => zeusApi.deleteSchool(id),
      mutationOptions
    )
  },

  loadZeusSchoolConfig(
    id: number,
    loadZeusSchoolQuery?: LoadZeusSchoolQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'get',
      '/zeus/schools/{id}',
      {
        id,
      },
      loadZeusSchoolQuery
    )
  },

  /**
   * @summary 大后台-学校单个查询
   * @param id
   * @param [loadZeusSchoolQuery]
   * @param [loadZeusSchoolQuery.expand]
   * @param [options]
   * @return AxiosResponsePromise<School>
   */
  loadZeusSchool<TQuery extends LoadZeusSchoolQuery>(
    id: number,
    loadZeusSchoolQuery?: TQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<Expand<School, TQuery['expand']>> {
    return axios(
      zeusApi.loadZeusSchoolConfig(id, loadZeusSchoolQuery, options)
    ).then(({ data: _data, ...rest }) => {
      const data = _data ? new School(_data) : _data
      return { data, ...rest }
    }) as any
  },

  useLoadZeusSchool<TQuery extends LoadZeusSchoolQuery>(
    {
      id,
      query,
    }: {
      id: number
      query?: TQuery
    },
    queryOptions?: QueryOptions
  ): UseQueryApiReturns<Expand<School, TQuery['expand']>> {
    return useQueryApi<Expand<School, TQuery['expand']>>(
      zeusApi.loadZeusSchool,
      'Zeus.loadZeusSchool',
      queryOptions,
      id,
      query
    )
  },

  updateZeusSchoolConfig(
    id: number,
    schoolReq: SchoolReq,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'put',
      '/zeus/schools/{id}',
      {
        id,
      },
      null,
      schoolReq,
      {
        format: 'json',
        isArray: false,
        defaults: {
          name: (v: any) => v || '',
          identity: (v: any) => v || '',
          url: (v: any) => v || '',
          systemVer: (v: any) => v || null,
          status: (v: any) => v || null,
          hidden: (v: any) => v || false,
          expiredAt: (v: any) => v || '',
          description: (v: any) => v || '',
          logo: (v: any) => v || '',
          bgImage: (v: any) => v || '',
          themeColor: (v: any) => v || '',
          openMsgCheck: (v: any) => v || false,
          manager: (v: any) => v || null,
        },
      }
    )
  },

  /**
   * @summary 大后台-学校单个编辑
   * @param id
   * @param schoolReq
   * @param [options]
   * @return AxiosResponsePromise<School>
   */
  updateZeusSchool(
    id: number,
    schoolReq: SchoolReq,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<School> {
    return axios(zeusApi.updateZeusSchoolConfig(id, schoolReq, options)).then(
      ({ data: _data, ...rest }) => {
        const data = _data ? new School(_data) : _data
        return { data, ...rest }
      }
    ) as any
  },

  useUpdateZeusSchool(mutationOptions?: MutationOptions) {
    return useMutationApi(
      ({ id, payload }: { id: number; payload: SchoolReq }) =>
        zeusApi.updateZeusSchool(id, payload),
      mutationOptions
    )
  },

  updateSchoolManagerPassConfig(
    mid: number,
    id: number,
    updatePassReq: UpdatePassReq,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'patch',
      '/zeus/schools/{id}/manager/{mid}/password',
      {
        mid,
        id,
      },
      null,
      updatePassReq,
      {
        format: 'json',
        isArray: false,
        defaults: {
          schoolId: (v: any) => v || 0,
        },
      }
    )
  },

  /**
   * @summary 大后台-学校管理员密码修改
   * @param mid
   * @param id
   * @param updatePassReq
   * @param [options]
   * @return AxiosResponsePromise<boolean>
   */
  updateSchoolManagerPass(
    mid: number,
    id: number,
    updatePassReq: UpdatePassReq,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<boolean> {
    return axios(
      zeusApi.updateSchoolManagerPassConfig(mid, id, updatePassReq, options)
    ) as any
  },

  useUpdateSchoolManagerPass(mutationOptions?: MutationOptions) {
    return useMutationApi(
      ({
        mid,
        id,
        payload,
      }: {
        mid: number
        id: number
        payload: UpdatePassReq
      }) => zeusApi.updateSchoolManagerPass(mid, id, payload),
      mutationOptions
    )
  },

  loginSchoolAsManagerConfig(
    id: number,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(options, 'post', '/zeus/schools/{id}/manager-login', {
      id,
    })
  },

  /**
   * @summary 大后台-系统用户以该校 root 管理员身份登录该学校
   * @param id
   * @param [options]
   * @return AxiosResponsePromise<User>
   */
  loginSchoolAsManager(
    id: number,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<User> {
    return axios(zeusApi.loginSchoolAsManagerConfig(id, options)).then(
      ({ data: _data, ...rest }) => {
        const data = _data ? new User(_data) : _data
        return { data, ...rest }
      }
    ) as any
  },

  useLoginSchoolAsManager(mutationOptions?: MutationOptions) {
    return useMutationApi(
      (id: number) => zeusApi.loginSchoolAsManager(id),
      mutationOptions
    )
  },
}

// generate uuid for every api function, useful for result-caching & other things
Object.keys(zeusApi).forEach((api) => {
  // @ts-ignore
  zeusApi[api]._name_ = `Zeus.${api}`
})
